import { Tooltip } from 'antd';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import DefaultButton from 'components/atoms/DefaultButton';
import WarningModal from 'components/modals/WarningModal';
import { useEnterpriseName } from 'store/modules/enterprise/hooks';
import { useIsLoading } from 'store/modules/loading/hooks';
import { deleteUser } from 'store/modules/users/actions';
import UserActionType from 'store/modules/users/constants';
import { useIsAnyPrimaryContact, useIsEnterpriseOwner } from 'store/modules/users/hooks';
import { MAX_WIDTH_MOBILE } from 'style/constants';
import { UserId } from 'types/models/user';

type UserRemoveButtonProps = {
  userId: UserId;
  userFullName: string;
  isFormSaving: boolean;
  afterUserRemoved?: () => void;
};

export function UserRemoveButton({ userId, userFullName, isFormSaving, afterUserRemoved }: UserRemoveButtonProps) {
  const [isRemoveUserModalVisible, setIsRemoveUserModalVisible] = useState(false);

  const isAnyPrimaryContact = useIsAnyPrimaryContact(userId);
  const isEnterpriseOwner = useIsEnterpriseOwner(userId);

  const enterpriseName = useEnterpriseName();
  const dispatch = useDispatch();

  const isLoading = useIsLoading(UserActionType.DELETE_USER_REQUEST);
  const isMobile = useMediaQuery({ maxWidth: MAX_WIDTH_MOBILE });

  const removeDisabledReason = (() => {
    if (isEnterpriseOwner) {
      return 'This user cannot be removed as they are the enterprise owner.';
    }
    if (isAnyPrimaryContact) {
      return 'This user is a site primary contact. You must update the site contact details to remove this user.';
    }
    return '';
  })();
  /** Disable the button when there is a reason to, or when the form is submitting its values. */
  const isButtonDisabled = !!removeDisabledReason || isFormSaving;
  const tooltipPlacement = isMobile ? 'bottomLeft' : 'right';

  return (
    <>
      <Tooltip
        title={removeDisabledReason}
        placement={tooltipPlacement}
      >
        {/* The div is here to receive the tooltip trigger event on behalf of the disabled button. */}
        <div>
          <DefaultButton
            danger
            disabled={isButtonDisabled}
            onClick={() => setIsRemoveUserModalVisible(true)}
          >
            REMOVE
          </DefaultButton>
        </div>
      </Tooltip>

      <WarningModal
        title={`Remove ${userFullName}?`}
        isOpen={isRemoveUserModalVisible}
        onConfirm={() => {
          const onSuccess = () => {
            if (afterUserRemoved) afterUserRemoved();
            else setIsRemoveUserModalVisible(false);
          };
          dispatch(deleteUser(userId, onSuccess));
        }}
        onDismiss={() => setIsRemoveUserModalVisible(false)}
        confirmText="Remove"
        isConfirmButtonLoading={isLoading}
      >
        <div className="body-copy">
          They will no longer be able to access{' '}
          <span className="body-emphasis-copy">{enterpriseName}</span>.
        </div>
      </WarningModal>
    </>
  );
}